import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, resolveUrl } from "../../../config/apis";
import AdminApi from "../../../config/apis/admin";
import StudentApi from "../../../config/apis/student";
import { GetUserId } from "../../../helper/helperFunction";
import { toast } from "react-toastify";
import {
  Box,
  Grid,
  InputLabel,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Rating,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
} from "@mui/material";
import styles from "./styles.module.css";

const FeedbackForm = ({
  toggleModal,
  sectionId: propSectionId,
  courseName,
  sectionName,
}) => {
  const [formData, setFormData] = useState({
    sectionId: null,
    courseAlignment: 0,
    instructorEngagement: 0,
    conceptExplanation: 0,
    instructorPerformance: "",
    learningMaterials: 0,
    assignmentsChallenging: "",
    itSupport: 0,
    administrativeTeam: 0,
    improvementAreas: "",
    majorProblems: "",
  });

  const [confirmModal, setConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  // Function to scroll to first error
  const scrollToFirstError = (errorFields) => {
    const firstErrorField = Object.keys(errorFields)[0];
    const errorElement = formRef.current?.querySelector(`[name="${firstErrorField}"]`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user makes a change
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (submitError) setSubmitError("");
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate rating fields (1-5 stars required)
    if (!formData.courseAlignment)
      newErrors.courseAlignment = "Please rate this question";
    if (!formData.instructorEngagement)
      newErrors.instructorEngagement = "Please rate this question";
    if (!formData.conceptExplanation)
      newErrors.conceptExplanation = "Please rate this question";
    if (!formData.learningMaterials)
      newErrors.learningMaterials = "Please rate this question";
    if (!formData.itSupport) newErrors.itSupport = "Please rate this question";
    if (!formData.administrativeTeam)
      newErrors.administrativeTeam = "Please rate this question";

    // Validate select/radio fields
    if (!formData.instructorPerformance)
      newErrors.instructorPerformance = "Please select an option";
    if (!formData.assignmentsChallenging)
      newErrors.assignmentsChallenging = "Please select Yes or No";

    // Validate text fields
    if (!formData.improvementAreas?.trim())
      newErrors.improvementAreas = "Please provide your input";
    if (!formData.majorProblems?.trim())
      newErrors.majorProblems = "Please provide your input";

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setConfirmModal(true);
    }
  };

  const studentId = GetUserId();

  // fetch cohort/section from student API if not provided via props
  useEffect(() => {
    const populateFromProps = () => {
      const finalSection = propSectionId ?? null;
      if (finalSection != null) {
        setFormData((prev) => ({ ...prev, sectionId: finalSection }));
        // eslint-disable-next-line no-console
        console.info("FeedbackForm populated section from props", {
          finalSection,
        });
        return true;
      }
      return false;
    };

    const fetchStudentData = async () => {
      try {
        const sid = studentId || 0;
        if (!sid) {
          // eslint-disable-next-line no-console
          console.warn("No student id available to fetch student data");
          return;
        }
        const res = await api.get(resolveUrl(AdminApi.GET_STUDENT_DATA));
        const student = res?.data?.data;
        if (student) {
          const finalSection = student.SectionID ?? null;
          setFormData((prev) => ({ ...prev, sectionId: finalSection }));
          // eslint-disable-next-line no-console
          console.info("FeedbackForm populated section from student API", {
            finalSection,
            student,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch student data for feedback form", err);
      }
    };

    const usedProps = populateFromProps();
    if (!usedProps) fetchStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propSectionId, studentId]);

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(""); // Clear any previous errors

    try {
      // Determine sectionId: prefer current state, otherwise fetch immediately and use returned value
      let finalSectionId = formData.sectionId ?? null;
      if (!finalSectionId) {
        try {
          const res = await api.get(StudentApi.GET_STUDENT_DATA);
          const student = res?.data?.data;
          if (student && student.SectionID) {
            finalSectionId = student.SectionID;
            // update UI state for visibility (non-blocking)
            setFormData((prev) => ({ ...prev, sectionId: finalSectionId }));
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("Could not fetch student data before submit", err);
        }
      }

      // Build payload matching backend expectations
      const payload = {
        student_id: studentId || 0,
        section_id: finalSectionId ?? null,
        contents_align_rating: formData.courseAlignment || 0,
        instructor_engagement_rating: formData.instructorEngagement || 0,
        explanation_clarity_rating: formData.conceptExplanation || 0,
        instructor_performance: formData.instructorPerformance || "",
        materials_helpful_rating: formData.learningMaterials || 0,
        assignments_challenging: formData.assignmentsChallenging === "Yes",
        it_support_rating: formData.itSupport || 0,
        admin_team_rating: formData.administrativeTeam || 0,
        areas_for_improvement: formData.improvementAreas || "",
        major_problems: formData.majorProblems || "",
        anonymous: true,
      };

      // Basic client-side validation
      if (!payload.student_id) {
        const msg = "Unable to determine logged in student. Please re-login.";
        console.error(msg, payload);
        setSubmitError(msg);
        setIsSubmitting(false);
        return;
      }

      // If cohort/section are missing, log a warning but allow submission (backend fields are nullable)
      if (payload.section_id == null) {
        // eslint-disable-next-line no-console
        console.warn("Submitting feedback without section_id", {
          section: payload.section_id,
        });
      }

      // log final payload for debugging
      // eslint-disable-next-line no-console
      console.debug("Submitting feedback payload", payload);

      const response = await api.post(
        resolveUrl(AdminApi.FEEDBACK_SUBMIT),
        payload
      );

      console.log("Feedback submitted:", response.data);

      // Show success notification
      toast.success(
        response.data.message || "Feedback submitted successfully!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );

      // Close the confirmation modal and parent modal
      setConfirmModal(false);
      toggleModal();
    } catch (error) {
      console.error("Error submitting feedback:", error);

      // Set error message to display in the confirmation dialog
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to submit feedback. Please try again later.";

      setSubmitError(errorMessage);

      // Optionally show error toast as well
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const instructorPerformanceOptions = [
    { value: "Superior", label: "Superior" },
    { value: "Solid", label: "Solid" },
    { value: "Needs Improvement", label: "Needs Improvement" },
    { value: "Unsatisfactory", label: "Unsatisfactory" },
  ];

  return (
    <>
      <Box>
        <Box sx={{ width: "100%" }}>
          <Grid container className="page-container">
            <Grid item xs={12} className="inner-container">
              <h2 className={styles.feedback_heading}>
                {courseName && (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                      Course Name:{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#C33F28" }}
                      >
                        {courseName}
                      </Typography>
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                      Section Name:{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#C33F28" }}
                      >
                        {sectionName}
                      </Typography>
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: "center",
                    mt: 1,
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Tell Us How We're Doing | Student Feedback Form
                </Typography>
              </h2>

              <Typography
                variant="body2"
                sx={{ mb: 3, color: "#666", lineHeight: 1.6 }}
              >
                Your feedback is precious to us as we continuously strive to
                enhance your learning experience. Please take a few moments to
                share your thoughts, suggestions, and concerns with us. Your
                responses will remain anonymous and will be used to make
                meaningful improvements to our courses.
              </Typography>
              <Box
                component="form"
                ref={formRef}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        1. Did the course contents align with your expectations
                        and learning goals?
                      </FormLabel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No
                        </Typography>
                        <Rating
                          name="courseAlignment"
                          value={formData.courseAlignment}
                          onChange={(event, newValue) =>
                            handleInputChange("courseAlignment", newValue)
                          }
                          size="large"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Yes
                        </Typography>
                      </Box>
                    </FormControl>
                    {errors.courseAlignment && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.courseAlignment}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 2 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        2. Were you able to engage with the instructor
                        effectively during the training sessions?
                      </FormLabel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No
                        </Typography>
                        <Rating
                          name="instructorEngagement"
                          value={formData.instructorEngagement}
                          onChange={(event, newValue) =>
                            handleInputChange("instructorEngagement", newValue)
                          }
                          size="large"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Yes
                        </Typography>
                      </Box>
                    </FormControl>
                    {errors.instructorEngagement && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.instructorEngagement}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 3 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        3. How well did the trainer explain complex concepts in
                        a simple manner?
                      </FormLabel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          Poor
                        </Typography>
                        <Rating
                          name="conceptExplanation"
                          value={formData.conceptExplanation}
                          onChange={(event, newValue) =>
                            handleInputChange("conceptExplanation", newValue)
                          }
                          size="large"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Excellent
                        </Typography>
                      </Box>
                    </FormControl>
                    {errors.conceptExplanation && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.conceptExplanation}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 4 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        4. Level of Performance of Instructor
                      </FormLabel>
                      <Select
                        name="instructorPerformance"
                        value={formData.instructorPerformance}
                        onChange={(e) =>
                          handleInputChange(
                            "instructorPerformance",
                            e.target.value
                          )
                        }
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select an option</em>
                        </MenuItem>
                        {instructorPerformanceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors.instructorPerformance && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.instructorPerformance}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 5 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        5. Were the learning materials (slides, examples)
                        helpful in understanding concepts?
                      </FormLabel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No
                        </Typography>
                        <Rating
                          value={formData.learningMaterials}
                          onChange={(event, newValue) =>
                            handleInputChange("learningMaterials", newValue)
                          }
                          size="large"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Yes
                        </Typography>
                      </Box>
                    </FormControl>
                    {errors.learningMaterials && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.learningMaterials}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 6 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        6. Were the assignments challenging enough to apply what
                        you learned?
                      </FormLabel>
                      <RadioGroup
                        value={formData.assignmentsChallenging}
                        onChange={(e) =>
                          handleInputChange(
                            "assignmentsChallenging",
                            e.target.value
                          )
                        }
                        row
                      >
                        <FormControlLabel
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="No"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                    {errors.assignmentsChallenging && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.assignmentsChallenging}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 7 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        7. How satisfied are you with the IT Support Team?
                      </FormLabel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          Poor
                        </Typography>
                        <Rating
                          value={formData.itSupport}
                          onChange={(event, newValue) =>
                            handleInputChange("itSupport", newValue)
                          }
                          size="large"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Excellent
                        </Typography>
                      </Box>
                    </FormControl>
                    {errors.itSupport && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.itSupport}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 8 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                        8. How well does the administrative team handle tasks
                        (organization, scheduling, coordination)?
                      </FormLabel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          Poor
                        </Typography>
                        <Rating
                          value={formData.administrativeTeam}
                          onChange={(event, newValue) =>
                            handleInputChange("administrativeTeam", newValue)
                          }
                          size="large"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Excellent
                        </Typography>
                      </Box>
                    </FormControl>
                    {errors.administrativeTeam && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.administrativeTeam}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 9 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel
                        shrink
                        sx={{
                          color: "#333",
                          fontWeight: 600,
                          position: "relative",
                          transform: "none",
                          mb: 2,
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                        }}
                      >
                        9. What areas could be improved to enhance the learning
                        experience for future students?
                      </InputLabel>
                      <TextField
                        name="improvementAreas"
                        multiline
                        rows={4}
                        value={formData.improvementAreas}
                        onChange={(e) =>
                          handleInputChange("improvementAreas", e.target.value)
                        }
                        placeholder="Please share your suggestions for improvement..."
                        sx={{ mt: 2 }}
                      />
                    </FormControl>
                    {errors.improvementAreas && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.improvementAreas}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Question 10 */}
                <Grid container sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel
                        shrink
                        sx={{
                          color: "#333",
                          fontWeight: 600,
                          position: "relative",
                          transform: "none",
                          mb: 2,
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                        }}
                      >
                        10. What major problems/difficulties did you face during
                        this course?
                      </InputLabel>
                      <TextField
                        name="majorProblems"
                        multiline
                        rows={4}
                        value={formData.majorProblems}
                        onChange={(e) =>
                          handleInputChange("majorProblems", e.target.value)
                        }
                        placeholder="Please describe any problems or difficulties you encountered..."
                        sx={{ mt: 2 }}
                      />
                    </FormControl>
                    {errors.majorProblems && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.majorProblems}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Submit Button */}
                <Grid container sx={{ mt: 4 }}>
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center" }}>
                      <Button
                        type="submit"
                        variant="contained"
                        className={styles.submit_btn}
                        sx={{
                          minWidth: 200,
                          py: 2,
                          background: "#c33f28",
                          "&:hover": {
                            background: "#a53524",
                          },
                        }}
                      >
                        Submit Feedback
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box
          sx={{ textAlign: "center", padding: "20px" }}
          className="action_btn"
        >
          <DialogTitle id="alert-dialog-title">Confirm Submission</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to submit your feedback? This action cannot
              be undone.
            </DialogContentText>
            {submitError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {submitError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              className="btn save"
              onClick={() => setConfirmModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className="btn cancel"
              autoFocus
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default FeedbackForm;
